import { DOCUMENT } from '@angular/common';
import { ErrorHandler, inject } from '@angular/core';
import { MessageService } from '@app/services/message.service';

/**
 * Custom error handler to reload page when the source has changed on server
 * to make sure the correct build chunks are loaded to keep linked modules accessible.
 *
 * @export
 * @class ChunkLoadingErrorHandler
 * @typedef {ChunkLoadingErrorHandler}
 * @implements {ErrorHandler}
 */
export class ChunkLoadingErrorHandler implements ErrorHandler {
    readonly #document = inject( DOCUMENT );
    #messageService = inject( MessageService );

    // handle trying to load a chunk that doesn't exist anymore
    handleError( error: any ) {
        if ( typeof error.message === 'string' ) {
            // Just looking at the first line of the error message (ignoring the call stack part),
            // which should contain a pattern that we are looking for.
            const firstLine = error.message.split( '\n' )[ 0 ];
            if ( firstLine?.match( /chunk-(.*?)\.js/ ) ) {
                this.#openErrorSnackBar();
                console.log( 'Chunk loading error' );
            }
        }

        // We still want to log every error
        console.error( error );
    }

    /**
     * when a chunk loading error is detected, show a snackbar to reload the page
    */
    #openErrorSnackBar(): void {
        const msg = {
            text: $localize`:@@msg-sources-changed-text:The page has been changed. Please reload the page.`,
            action: $localize`:@@msg-sources-changed-action:Reload`
        };
        const snackbarRef = this.#messageService.action( msg );
        // when the user clicks the reload action or outside the snackbar, the page is reloaded
        snackbarRef.onAction().subscribe( () => {
            this.#document.location.reload();
        } );
    }
}
